import { slateToHtml } from "@slate-serializers/html";
import { Descendant } from "slate";
import { jsx } from "slate-hyperscript";
import { CustomText } from "../types";
import { serializerConfig } from "./serializerConfig";

/**
 * mapping from html tag to slate jason element type
 */
export const ELEMENT_TAGS: any = {
  A: (el: HTMLElement) => ({ type: 'link', url: el.getAttribute('href') }),
  BLOCKQUOTE: () => ({ type: 'blockquote' }),
  H1: () => ({ type: 'h1' }),
  H2: () => ({ type: 'h2' }),
  H3: () => ({ type: 'h3' }),
  H4: () => ({ type: 'h4' }),
  H5: () => ({ type: 'h5' }),
  H6: () => ({ type: 'h6' }),
  IMG: (el: HTMLElement) => ({ type: 'image', url: el.getAttribute('src') }),
  LI: () => ({ type: 'list-item' }),
  OL: () => ({ type: 'numbered-list' }),
  P: () => ({ type: 'paragraph' }),
  PRE: () => ({ type: 'code' }),
  UL: () => ({ type: 'bulleted-list' }),
}

/**
 * mapping from html tag to slate jason tag type
 */
// COMPAT: `B` is omitted here because Google Docs uses `<b>` in weird ways.
export const TEXT_TAGS: any = {
  CODE: () => ({ code: true }),
  DEL: () => ({ strikethrough: true }),
  EM: () => ({ italic: true }),
  I: () => ({ italic: true }),
  S: () => ({ strikethrough: true }),
  B: () => ({ bold: true }),
  STRONG: () => ({ bold: true }),
  U: () => ({ underline: true }),
}

/**
 * could return any of CustomEditor | CustomElement | CustomText | EmptyText | Descendant[]
 * 
 * image representation in slate:
 * {
    type: 'image',
    url: 'https://source.unsplash.com/zOwZKwZOZq8',
    children: [{ text: '' }],
  }

  {"type":"image","url":"https://www.portplay.org/resources/event-edit/978-1-76358-980-3.png","children":[{"text":""}]}


  list representation in slate

[
  {"type":"h2","children":[
    {"text":"Heading"}]},{"type":"paragraph","children":[{"text":"Come play the "},{"text":"original D&D","italic":true},{"text":" knockoff, when D&D "},{"type":"link","url":"http://apple.com/","children":[{"text":"do the link again"}]},{"text":" into "},{"text":"its first indenture","bold":true},{"text":" to "},{"text":"an evil ","strikethrough":true},{"text":"corporation"}]},
    {"type":"bulleted-list","children":[
      {"type":"list-item","children":[{"text":"listing something"}]},
      {"type":"list-item","children":[{"text":"and something else"}]},
      {"type":"list-item","children":[{"text":""}]},
      {"type":"list-item","children":[{"text":""}]}
    ]}]

 * @param html 
 * @returns any: The accepted way of deserialising to Slate format returns a variety of types
 */
export const toSlateJson = (html?: string): any => {

  if (html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html')

    const beSlated = deserialize(parsed.body);
    return beSlated;
    
  } else {
    return [{text:''}];
  }

}

/**
 * Serializes the Slate JSON representation of the Editable content into HTML. The HTML can be stored as the content of
 * the overall form model that the Slate Editable forms a part, and later presented as innerHTML in display functions.
 * 
 * The serializerConfig passed into the 'slateToHtml(slateJson, serializerConfig)' mainline has been customized from the default configuration
 * of the slater-serializer/html library to include serializing images (which would otherwise be ignored).
 * @param slateJson the Slate JSON representation of the Editable content
 * @returns rendered html
 */
export const toHtml = (slateJson: any): string => {
  console.log(JSON.stringify(slateJson));
  const html =  slateToHtml(slateJson, serializerConfig);
  console.log(html);
  return html;
}

export const deserialize = (el: HTMLElement, markAttributes = {}): any => {

  //let thisLevelDecendents = new Array<Descendant>()

  if (el.nodeType === Node.TEXT_NODE) {
    return jsx('text', markAttributes, el.textContent)
  } else if (el.nodeType !== Node.ELEMENT_NODE) {
    return [] as Descendant[];
  }
  const nodeAttributes = { ...markAttributes }

  const { nodeName } = el
  let parent = el

  if (
    nodeName === 'PRE' &&
    el.childNodes[0] &&
    el.childNodes[0].nodeName === 'CODE'
  ) {
    parent = el.childNodes[0] as HTMLElement;
  }

  const children = Array.from(el.childNodes)
    .map(node => deserialize(node as HTMLElement, nodeAttributes))
    .flat()

  if (children.length === 0) {
    children.push(jsx('text', nodeAttributes, ''))
  }

  if (ELEMENT_TAGS[nodeName]) {
    const attrs = ELEMENT_TAGS[nodeName](el)
    return jsx('element', attrs, children)
  }

  if (TEXT_TAGS[nodeName]) {
    const attrs = TEXT_TAGS[nodeName](el)
    return children.map(child => jsx('text', attrs, child))
  }

  // define attributes for text nodes
  switch (el.nodeName) {
    // Elements:
    case 'BODY':
      return jsx('fragment', {}, children);
    case 'H1':
      return jsx('element', { type: 'heading', number: 1 }, children);
    case 'H2':
      return jsx('element', { type: 'heading', number: 2 }, children);
    case 'H3':
      return jsx('element', { type: 'heading', number: 3 }, children);
    case 'H4':
      return jsx('element', { type: 'heading', number: 4 }, children);
    case 'H5':
      return jsx('element', { type: 'heading', number: 4 }, children);
    case 'H6':
      return jsx('element', { type: 'heading', number: 4 }, children);
    case 'BLOCKQUOTE':
      return jsx('element', { type: 'quote' }, children)
    case 'P':
      return jsx('element', { type: 'paragraph' }, children)
    case 'A':
      return jsx( 'element', { type: 'link', url: el.getAttribute('href'), target: el.getAttribute('target')}, children );
    case 'IMAGE':
      return jsx( 'element', { type: 'image', url: el.getAttribute('href') }, children );
    case 'BR':
      return '\n'
    // Leaves:
    case 'STRONG':
      return { text: el.textContent, bold: true } as CustomText;
    case 'EM':
      return { text: el.textContent, italic: true } as CustomText;
    case 'U':
      return { text: el.textContent, underline: true } as CustomText;
    default:
      return children.flat();
  }
}

const allowedSchemes = ['http:', 'https:', 'mailto:', 'tel:']

export const makeSafeUrl = (unsafe: string): string => {
  let parsedUrl: URL = new URL(unsafe);
  try {
    parsedUrl = new URL(unsafe)
    // eslint-disable-next-line no-empty
  } catch {}
  if (parsedUrl && allowedSchemes.includes(parsedUrl.protocol)) {
    return parsedUrl.href;
  }
  return parsedUrl.href;

}
